import { isDefined } from "@omni/utils/helper.utils";
import {
    createContext,
    PropsWithChildren,
    RefObject,
    use,
    useState
} from "react";

import { SearchItem } from "../StoreSearchInput/storeSearchInput.types";
import { useDirections } from "./hooks/useDirections.hook";

type StoreDirectionsContextValue = {
    storeName: string;
    origin: SearchItem | undefined;
    distance: number | undefined;
    setOrigin: (origin: SearchItem) => void;
    resetOrigin: () => void;
};

type StoreDirectionsContextProviderProps = PropsWithChildren<{
    storeName: string;
    mapElementRef: RefObject<HTMLDivElement | null>;
    destination: woosmap.map.LatLngLiteral;
}>;

export const StoreDirectionsContext = createContext<
    StoreDirectionsContextValue | undefined
>(undefined);
StoreDirectionsContext.displayName = "StoreDirectionsContext";

export const StoreDirectionsContextProvider = ({
    storeName,
    destination,
    mapElementRef,
    children
}: StoreDirectionsContextProviderProps) => {
    const [origin, setOrigin] = useState<SearchItem | undefined>(undefined);

    const originLatLng: woosmap.map.LatLngLiteral | undefined =
        isDefined(origin?.latitude) && isDefined(origin.longitude)
            ? {
                  lat: origin.latitude,
                  lng: origin.longitude
              }
            : undefined;

    const resetOrigin = () => setOrigin(undefined);

    const route = useDirections({
        origin: originLatLng,
        destination: destination,
        mapElementRef: mapElementRef
    });

    const distance = route?.legs.at(0)?.distance.value;

    return (
        <StoreDirectionsContext
            value={{
                storeName: storeName,
                origin: origin,
                distance: distance,
                setOrigin: setOrigin,
                resetOrigin: resetOrigin
            }}
        >
            {children}
        </StoreDirectionsContext>
    );
};

export const useStoreDirectionsContext = () => {
    const context = use<StoreDirectionsContextValue | undefined>(
        StoreDirectionsContext
    );

    if (!isDefined(context)) {
        throw new Error(`${StoreDirectionsContext.displayName} is undefined.`);
    }

    return context;
};
